import type {
  HTMLAttributes,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
} from "react";

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  children: ReactNode;
}

export function Table({ children, className, ...rest }: TableProps) {
  const classes = ["qr-table"];
  if (className) classes.push(className);

  return (
    <div className="qr-table-wrap">
      <table className={classes.join(" ")} {...rest}>
        {children}
      </table>
    </div>
  );
}

export interface TableHeaderProps
  extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export function TableHeader({
  children,
  className,
  ...rest
}: TableHeaderProps) {
  const classes = ["qr-table-header"];
  if (className) classes.push(className);

  return (
    <thead className={classes.join(" ")} {...rest}>
      {children}
    </thead>
  );
}

export interface TableBodyProps
  extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export function TableBody({ children, className, ...rest }: TableBodyProps) {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
}

export function TableRow({ children, className, ...rest }: TableRowProps) {
  const classes = ["qr-table-row"];
  if (className) classes.push(className);

  return (
    <tr className={classes.join(" ")} {...rest}>
      {children}
    </tr>
  );
}

export interface TableCellTruncateProps
  extends HTMLAttributes<HTMLSpanElement> {
  children?: ReactNode;
}

// TableCell'in `truncate` prop'unun kullandığı aynı span — çok parçalı
// hücrelerde (ör. bir satır kısaltılmış metin + altında kısaltılmamış ikinci
// bir satır) yalnızca ilgili parçayı sarmak için doğrudan da kullanılabilir.
export function TableCellTruncate({
  className,
  children,
  ...rest
}: TableCellTruncateProps) {
  const classes = ["qr-table-cell-truncate-inner"];
  if (className) classes.push(className);

  return (
    <span className={classes.join(" ")} {...rest}>
      {children}
    </span>
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Header hücresi için "th", veri hücresi için "td" (varsayılan). */
  as?: "td" | "th";
  /** Tek satırlık içeriği ellipsis ile kısaltır (ör. uzun işletme adı). */
  truncate?: boolean;
  children?: ReactNode;
}

export function TableCell({
  as: Tag = "td",
  truncate,
  className,
  children,
  ...rest
}: TableCellProps) {
  const classes = ["qr-table-cell"];
  if (className) classes.push(className);

  return (
    <Tag className={classes.join(" ")} {...rest}>
      {truncate ? <TableCellTruncate>{children}</TableCellTruncate> : children}
    </Tag>
  );
}

export interface TableEmptyStateProps {
  /** Tabloyu kaç kolon kapladığı — colSpan için gerekli. */
  colSpan: number;
  children: ReactNode;
}

export function TableEmptyState({ colSpan, children }: TableEmptyStateProps) {
  return (
    <tr className="qr-table-empty-row">
      <td colSpan={colSpan} className="qr-table-empty-cell">
        {children}
      </td>
    </tr>
  );
}
